/**
 * USAGE: this can be used two ways:
 *  1. As a full page route (legacy) — if no `onClose` prop is passed, picking
 *     a city falls back to `navigate(-1)` to pop the route.
 *  2. Inside a modal (e.g. CustomerHeader) — pass `onClose` and it will be
 *     called after dispatching the city selection instead of navigating.
 *
 * FIX: city search only exists as a province-scoped lookup
 * (`getFilteredCities(provinceName, searchText)`) — there is no free-text
 * "search every city regardless of province" endpoint anywhere in the app.
 * This previously tried to search without a province and always fell back
 * to an empty result set (see the removed onChangeSearch stub). Ported the
 * same province-select-then-search flow that already works correctly in
 * CityPickerButtonModal, reusing the identical redux actions/selectors.
 */
import React, { useEffect, useState } from 'react';

import CSSModule from 'react-css-modules';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { actions as cityActions } from 'coupon-common/src/modules/cities';
import { actions as locationActions, duck as locationsDuck } from 'coupon-common/src/modules/locations';

import styles from './LocationSearch.module.scss';
import messages from './messages';

const DEFAULT_CITY_STORAGE_KEY = 'DEFAULT_CITY';
const provincesKey = locationsDuck.provincesEntity.key;
const citiesKey = locationsDuck.citiesEntity.key;

const LocationSearch = ({ onClose }) => {
  const [provinceId, setProvinceId] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();

  useEffect(() => {
    dispatch(locationActions.getProvinces());
  }, [dispatch]);

  const locations = useSelector(state => state.locations);
  const provinceOptions = locations.result?.[provincesKey]?.results?.map(id => locations.entities[provincesKey]?.[id]) || [];

  const citiesState = useSelector(state => state.locations.result?.[citiesKey]);
  const cityEntities = useSelector(state => state.locations.entities?.[citiesKey]) || {};
  const results = citiesState?.results?.map(id => cityEntities[id]).filter(Boolean) || [];

  // Same pattern as CityPickerButtonModal: re-fetch whenever the chosen
  // province or the search text changes. No-ops until a province is picked.
  useEffect(() => {
    const province = provinceOptions.find(p => String(p?.id) === String(provinceId));
    if (province) {
      dispatch(locationActions.getFilteredCities(province.name, searchValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provinceId, searchValue]);

  const onPressItem = item => {
    dispatch(cityActions.setActiveCity(item.name, item.id));
    try {
      localStorage.setItem(DEFAULT_CITY_STORAGE_KEY, JSON.stringify(item));
    } catch (e) {
      console.warn('Could not persist default city to localStorage', e);
    }
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  return (
    <div styleName="page">
      <label styleName="field-label" htmlFor="location-search-province">
        Province
      </label>
      <select
        id="location-search-province"
        styleName="province-select"
        value={provinceId}
        onChange={e => {
          setProvinceId(e.target.value);
          setSearchValue('');
        }}
      >
        <option value="" disabled>
          Select a province
        </option>
        {provinceOptions.map(province => (
          <option key={province.id} value={province.id}>
            {province.name}
          </option>
        ))}
      </select>

      <div styleName="search-bar">
        <span styleName="search-icon" aria-hidden="true">
          🔍
        </span>
        <input
          type="text"
          placeholder={intl.formatMessage(messages.search)}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value.trim())}
          styleName="search-input"
          disabled={!provinceId}
        />
      </div>

      <p styleName="result-text">
        <FormattedMessage {...messages.searchResultText} />
      </p>

      <div styleName="results-list">
        {provinceId && results.length === 0 && searchValue && (
          <p styleName="empty-text">No cities found.</p>
        )}
        {results.map(item => (
          <button key={item.id} type="button" styleName="result-item" onClick={() => onPressItem(item)}>
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CSSModule(LocationSearch, styles, { allowMultiple: true });